import { REPUTATION } from '../../supabase/functions/_shared/reputation'

/**
 * 🎓 Günüm Var! - Finansal Sabitler
 * Para birimi, kur ve finansal hesaplama sabitleri
 */

import type { Currency } from '@/types/database.types'

// ============================================
// PARA BİRİMLERİ
// ============================================

/** Varsayılan para birimi */
export const DEFAULT_CURRENCY: Currency = 'TRY'

/** Desteklenen para birimleri */
export const SUPPORTED_CURRENCIES: readonly Currency[] = [
  'TRY',
  'USD',
  'EUR',
  'GRAM_GOLD',
  'GRAM_SILVER',
] as const

/** Para birimi bilgileri */
export const CURRENCY_INFO = {
  TRY: {
    code: 'TRY',
    symbol: '₺',
    name: 'Türk Lirası',
    locale: 'tr-TR',
    decimals: 2,
  },
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'Amerikan Doları',
    locale: 'en-US',
    decimals: 2,
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    locale: 'de-DE',
    decimals: 2,
  },
  GRAM_GOLD: {
    code: 'GRAM_GOLD',
    symbol: 'gr',
    name: 'Gram Altın',
    locale: 'tr-TR',
    decimals: 4, // Altın için 4 ondalık
  },
  GRAM_SILVER: {
    code: 'GRAM_SILVER',
    symbol: 'gr',
    name: 'Gram Gümüş',
    locale: 'tr-TR',
    decimals: 2,
  },
} as const

// ============================================
// TEMA RENKLERİ (Para Birimine Göre)
// ============================================

/** Para birimine göre tema renkleri */
export const CURRENCY_THEME: Record<Currency, { primary: string; secondary: string; gradient: string }> = {
  TRY: {
    primary: '#C30010', // Crimson
    secondary: '#E63946',
    gradient: 'linear-gradient(135deg, #C30010 0%, #F94144 100%)',
  },
  USD: {
    primary: '#13EC80', // Emerald Green
    secondary: '#00BB7E',
    gradient: 'linear-gradient(135deg, #13EC80 0%, #00BB7E 100%)',
  },
  EUR: {
    primary: '#1E3A8A', // Royal Blue
    secondary: '#2563EB',
    gradient: 'linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%)',
  },
  GRAM_GOLD: {
    primary: '#D4AF37', // Antique Gold
    secondary: '#C5A028',
    gradient: 'linear-gradient(135deg, #D4AF37 0%, #B8962E 100%)',
  },
  GRAM_24_GOLD: {
    primary: '#D4AF37',
    secondary: '#C5A028',
    gradient: 'linear-gradient(135deg, #D4AF37 0%, #B8962E 100%)',
  },
  GRAM_22_GOLD: {
    primary: '#D4AF37',
    secondary: '#C5A028',
    gradient: 'linear-gradient(135deg, #D4AF37 0%, #B8962E 100%)',
  },
  QUARTER_GOLD: {
    primary: '#D4AF37',
    secondary: '#C5A028',
    gradient: 'linear-gradient(135deg, #D4AF37 0%, #B8962E 100%)',
  },
  HALF_GOLD: {
    primary: '#D4AF37',
    secondary: '#C5A028',
    gradient: 'linear-gradient(135deg, #D4AF37 0%, #B8962E 100%)',
  },
  FULL_GOLD: {
    primary: '#D4AF37',
    secondary: '#C5A028',
    gradient: 'linear-gradient(135deg, #D4AF37 0%, #B8962E 100%)',
  },
  GRAM_SILVER: {
    primary: '#A8A9AD', // Metallic Silver
    secondary: '#C0C0C0',
    gradient: 'linear-gradient(135deg, #A8A9AD 0%, #C0C0C0 100%)',
  },
}

// ============================================
// KUR KAYNAKLARI
// ============================================

/** Kur veri kaynakları */
export const RATE_SOURCES = {
  primary: 'yahoo_finance',
  fallback: 'exchangerate_api',
} as const

/** Yahoo Finance sembolleri */
export const YAHOO_SYMBOLS = {
  USD_TRY: 'USDTRY=X',
  EUR_TRY: 'EURTRY=X',
  GBP_TRY: 'GBPTRY=X',
  GOLD_USD: 'GC=F',
  SILVER_USD: 'SI=F',
} as const

// ============================================
// ALTIN HESAPLAMA SABİTLERİ
// ============================================

/** 1 ons = kaç gram */
export const OUNCE_TO_GRAM = 31.1035

/** 24 ayar altın saflık oranı */
export const GOLD_24K_PURITY = 0.9999

/** 22 ayar altın saflık oranı */
export const GOLD_22K_PURITY = 0.9167

/** 21 ayar altın saflık oranı */
export const GOLD_21K_PURITY = 0.875

/** 18 ayar altın saflık oranı */
export const GOLD_18K_PURITY = 0.75

/** Gümüş saflık oranı */
export const SILVER_PURITY = 0.999

// ============================================
// İTİBAR PUAN DEĞİŞİMLERİ
// ============================================

/**
 * Ödeme aksiyonlarına göre puan değişimleri.
 *
 * ⚠️ BU SAYILAR BURADA TANIMLANMAZ. Puanı gerçekten veren yer edge
 * function'daki `supabase/functions/_shared/reputation.ts`; buradaki tablo
 * yalnız EKRANDA ne yazılacağını bilmek için ondan TÜRETİLİR.
 *
 * Eskiden iki tablo elle kopyalanıyordu ve ayrışmıştı: motorda olan iki ceza
 * türü (otomatik onay, zorla ilerletme) istemcide hiç yoktu. Artık ayrışma
 * mümkün değil — tek kaynak motorun kendisi.
 *
 * (Aynı desen `src/utils/abacus/lifecycle.ts` içinde de kullanılıyor.)
 */
export const REPUTATION_CHANGES = {
  /** Zamanında ödeme yapıldı */
  onTimePayment: REPUTATION.ON_TIME_PAYMENT,
  /** Erken ödeme bonusu — zamanında puanının ÜSTÜNE eklenir, onun yerine geçmez */
  earlyBirdPayment: REPUTATION.EARLY_BIRD_PAYMENT,
  /** Geç ödeme yapıldı (1-3 gün) */
  latePayment: REPUTATION.LATE_PAYMENT,
  /** Çok geç ödeme (3+ gün) */
  veryLatePayment: REPUTATION.VERY_LATE_PAYMENT,
  /** Ödeme reddedildi */
  paymentRejected: REPUTATION.PAYMENT_REJECTED,
  /** Döngü başarıyla tamamlandı */
  cycleCompleted: REPUTATION.CYCLE_COMPLETED,
  /** Gruptan ayrıldı (döngü ortasında) */
  leftMidCycle: REPUTATION.LEFT_MID_CYCLE,
  /** Streak bonusu (ardışık zamanında ödeme, 3'te bir) */
  streakBonus: REPUTATION.STREAK_BONUS,
  /** Profil %100 tamamlandı (sadece bir kez) */
  profileComplete: REPUTATION.PROFILE_COMPLETE,
  /** Davet edilen kullanıcı başarıyla gruba katıldı */
  referralJoined: REPUTATION.REFERRAL_JOINED,
  /** Düşen puandan sonra toparlama bonusu */
  comebackBonus: REPUTATION.COMEBACK_BONUS,
  /** Alacaklı onaylamadı, sistem otomatik onayladı — ALACAKLI cezası */
  autoConfirmReceiverPenalty: REPUTATION.AUTO_CONFIRM_RECEIVER_PENALTY,
  /** Döngü zorla ilerletildi — cezası */
  forceAdvancePenalty: REPUTATION.FORCE_ADVANCE_PENALTY,
} as const

// ============================================
// IBAN DOĞRULAMA
// ============================================

/** Türkiye IBAN formatı */
export const TR_IBAN_REGEX = /^TR\d{2}\d{4}\d{16}$/

/** IBAN uzunluğu (Türkiye) */
export const TR_IBAN_LENGTH = 26
